import dayjs from 'dayjs';
import type { AggregationResultsCategoryRow } from '../../../../../../store/api/monitoring-api';

export type AspectsDynamicsSentimentKey = 'negative' | 'neutral' | 'positive';

export type AspectsDynamicsGroupingInterval = 'day' | 'week' | 'month';

function sentimentField(key: AspectsDynamicsSentimentKey): 'positive_count' | 'neutral_count' | 'negative_count' {
  switch (key) {
    case 'positive':
      return 'positive_count';
    case 'neutral':
      return 'neutral_count';
    default:
      return 'negative_count';
  }
}

function sumAspectSentiment(row: AggregationResultsCategoryRow, sentiment: AspectsDynamicsSentimentKey): number {
  const field = sentimentField(sentiment);
  return (row.aspects ?? []).reduce((acc, a) => acc + (Number(a[field]) || 0), 0);
}

export function formatAspectsDynamicsBucketLabel(
  begin: number,
  end: number | undefined,
  interval: AspectsDynamicsGroupingInterval,
): string {
  const b = dayjs.unix(begin);
  if (interval === 'month') {
    return b.format('MM.YYYY');
  }
  if (interval === 'week' && end != null && Number.isFinite(end)) {
    const e = dayjs.unix(end);
    return `${b.format('DD.MM')}–${e.format('DD.MM')}`;
  }
  return b.format('DD.MM.YYYY');
}

export function buildAspectsDynamicsByCategoryChartData(
  rows: AggregationResultsCategoryRow[],
  categoryNamesById: Record<number, string>,
  groupingInterval: AspectsDynamicsGroupingInterval,
  sentiment: AspectsDynamicsSentimentKey,
): { xCategories: string[]; series: { name: string; data: number[] }[] } {
  const endByBegin = new Map<number, number>();
  const cell = new Map<number, Map<number, number>>();

  for (const row of rows) {
    const begin = row.begin;
    if (begin == null || !Number.isFinite(begin)) continue;
    const catId = Number(row.category_id);
    if (!Number.isFinite(catId)) continue;

    const v = sumAspectSentiment(row, sentiment);
    if (!cell.has(begin)) cell.set(begin, new Map());
    const m = cell.get(begin)!;
    m.set(catId, (m.get(catId) || 0) + v);

    const end = row.end;
    if (end != null && Number.isFinite(end)) {
      const prev = endByBegin.get(begin);
      endByBegin.set(begin, prev == null ? end : Math.max(prev, end));
    }
  }

  const sortedBegins = [...cell.keys()].sort((a, b) => a - b);
  if (sortedBegins.length === 0) {
    return { xCategories: [], series: [] };
  }

  const categoryIds = new Set<number>();
  for (const m of cell.values()) {
    for (const id of m.keys()) categoryIds.add(id);
  }

  const sortedCategoryIds = [...categoryIds].sort((a, b) => {
    const na = categoryNamesById[a] ?? '';
    const nb = categoryNamesById[b] ?? '';
    const cmp = na.localeCompare(nb, 'ru');
    if (cmp !== 0) return cmp;
    return a - b;
  });

  const xCategories = sortedBegins.map((beg) =>
    formatAspectsDynamicsBucketLabel(beg, endByBegin.get(beg), groupingInterval),
  );

  const series = sortedCategoryIds.map((catId) => ({
    name: categoryNamesById[catId] ?? `Категория ${catId}`,
    data: sortedBegins.map((beg) => cell.get(beg)?.get(catId) ?? 0),
  }));

  return { xCategories, series };
}
